import axios from 'axios';

// Configuración base de la API - Usar configuración global
const API_BASE_URL = window.APP_CONFIG?.API_URL || 'https://web-production-910f8.up.railway.app/api';

console.log('🔧 Configurando Metal Prices Service con URL:', API_BASE_URL);

// Crear instancia de axios específica para precios de metales
const metalPricesApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // Timeout más largo para precios de metales
});

// Interceptor para manejar errores específicos de precios de metales
metalPricesApi.interceptors.response.use(
  (response) => {
    console.log('✅ Response interceptor - Status:', response.status);
    console.log('📊 Response data:', response.data);
    return response;
  },
  (error) => {
    console.error('❌ Metal Prices API Error:', error);
    
    if (error.response?.status === 404) {
      console.error('Endpoint de precios no encontrado');
    } else if (error.response?.status === 503) {
      console.error('Servicio de precios no disponible');
    } else if (error.code === 'ECONNABORTED') {
      console.error('Timeout obteniendo precios de metales');
    }
    
    return Promise.reject(error);
  }
);

// Función para transformar datos del backend al formato del frontend
const transformBackendData = (backendData) => {
  if (!backendData || !backendData.prices) {
    console.warn('⚠️  Datos del backend no tienen la estructura esperada:', backendData);
    return {};
  }

  const transformed = {};
  
  backendData.prices.forEach(priceData => {
    const metalName = priceData.metal;
    if (metalName) {
      // Mapear nombres de metales del backend al frontend
      const metalMapping = {
        'copper': 'cobre',
        'aluminum': 'aluminio', 
        'nickel': 'niquel',
        'lead': 'plomo',
        'tin': 'estano',
        'gold': 'oro',
        'silver': 'plata',
        'platinum': 'platino',
        'palladium': 'paladio'
      };
      
      const frontendMetalName = metalMapping[metalName] || metalName;
      
      // Convertir precio según la unidad original
      let priceMxnKg = null;
      if (priceData.precio && priceData.precio > 0) {
        const usdToMxn = 20; // Tipo de cambio USD a MXN
        const unidad = priceData.unidad || 'lb';
        
        if (unidad === 'oz') {
          // Para metales preciosos (USD/oz -> MXN/kg)
          // 1 oz = 0.0283495 kg
          const ozToKg = 0.0283495;
          priceMxnKg = (priceData.precio * usdToMxn / ozToKg).toFixed(2);
        } else {
          // Para metales industriales (USD/lb -> MXN/kg)
          // 1 lb = 0.453592 kg
          const lbToKg = 0.453592;
          priceMxnKg = (priceData.precio * usdToMxn / lbToKg).toFixed(2);
        }
      } else if (priceData.price_usd && priceData.price_usd > 0) {
        // Fallback al formato anterior (asumiendo USD/lb)
        const usdToMxn = 20;
        const lbToKg = 0.453592;
        priceMxnKg = (priceData.price_usd * usdToMxn / lbToKg).toFixed(2);
      }
      
      // Determinar tendencia basada en el cambio
      let trend = 'up';
      const changePercent = priceData.cambio_porcentual || priceData.change_percent || 0;
      if (changePercent < 0) trend = 'down';
      
      transformed[frontendMetalName] = {
        price: priceMxnKg,
        change: `${changePercent > 0 ? '+' : ''}${changePercent?.toFixed(2) || '0.00'}%`,
        trend: trend,
        source: priceData.fuente || priceData.source || 'https://finance.yahoo.com'
      };
    }
  });
  
  console.log('🔄 Datos transformados:', transformed);
  return transformed;
};

// Servicios de precios de metales
export const metalPricesService = {
  // Obtener precios en tiempo real con fallback
  getRealtimePrices: async () => {
    try {
      console.log('📡 getRealtimePrices: Llamando a /metal-prices/realtime/');
      const response = await metalPricesApi.get('/metal-prices/realtime/');
      console.log('✅ getRealtimePrices: Respuesta exitosa:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ getRealtimePrices: Error:', error);
      throw error;
    }
  },

  // Obtener todos los precios de metales (legacy)
  getAllPrices: async () => {
    try {
      console.log('📡 getAllPrices: Llamando a /market/prices/');
      const response = await metalPricesApi.get('/market/prices/');
      console.log('✅ getAllPrices: Respuesta exitosa:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ getAllPrices: Error:', error);
      throw error;
    }
  },

  // Obtener resumen de precios
  getSummary: async () => {
    try {
      console.log('📡 getSummary: Llamando a /market/prices/summary/');
      const response = await metalPricesApi.get('/market/prices/summary/');
      console.log('✅ getSummary: Respuesta exitosa:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ getSummary: Error:', error);
      throw error;
    }
  },

  // Obtener precio de un metal específico
  getMetalPrice: async (metalName) => {
    try {
      console.log(`📡 getMetalPrice: Llamando a /market/prices/metal/${metalName}/`);
      const response = await metalPricesApi.get(`/market/prices/metal/${metalName}/`);
      console.log(`✅ getMetalPrice: Respuesta exitosa para ${metalName}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ getMetalPrice: Error para ${metalName}:`, error);
      throw error;
    }
  },

  // Forzar actualización de precios
  forceUpdate: async () => {
    try {
      console.log('📡 forceUpdate: Llamando a /metal-prices/force_update/');
      const response = await metalPricesApi.post('/metal-prices/force_update/');
      console.log('✅ forceUpdate: Respuesta exitosa:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ forceUpdate: Error:', error);
      throw error;
    }
  },

  // Obtener precios con reintentos (usando la nueva API)
  getPricesWithRetry: async (maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 getPricesWithRetry: Intento ${attempt} de ${maxRetries} para obtener precios...`);
        console.log(`📡 Llamando a ${API_BASE_URL}/metal-prices/realtime/`);
        
        const response = await metalPricesApi.get('/metal-prices/realtime/');
        
        console.log(`✅ getPricesWithRetry: Intento ${attempt} exitoso`);
        console.log('📊 Datos de la respuesta:', response.data);
        
        // Verificar estructura de datos
        if (response.data && response.data.prices) {
          console.log(`📋 Número de precios en la respuesta: ${response.data.prices.length}`);
          if (response.data.prices.length > 0) {
            console.log('🔍 Primer precio:', response.data.prices[0]);
          }
        } else {
          console.warn('⚠️  Respuesta no tiene la estructura esperada:', response.data);
        }
        
        return response.data;
      } catch (error) {
        lastError = error;
        console.warn(`❌ getPricesWithRetry: Intento ${attempt} falló:`, error.message);
        
        if (attempt < maxRetries) {
          // Esperar antes del siguiente intento (backoff exponencial)
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          console.log(`⏳ Esperando ${delay}ms antes del siguiente intento...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    console.error('❌ getPricesWithRetry: Todos los intentos fallaron');
    throw lastError;
  },

  // Nueva función para obtener precios en formato del frontend
  getFrontendPrices: async () => {
    try {
      console.log('📡 getFrontendPrices: Obteniendo precios del backend...');
      const response = await metalPricesApi.get('/market/prices/');
      console.log('✅ getFrontendPrices: Respuesta del backend:', response.data);
      
      // Transformar datos al formato del frontend
      const transformedData = transformBackendData(response.data);
      console.log('🔄 getFrontendPrices: Datos transformados:', transformedData);
      
      return transformedData;
    } catch (error) {
      console.error('❌ getFrontendPrices: Error:', error);
      // Retornar datos por defecto en caso de error
      return {
        aluminio: { price: 45.80, change: '-1.2%', trend: 'down' },
        cobre: { price: 89.20, change: '+0.8%', trend: 'up' },
        niquel: { price: 156.80, change: '+2.1%', trend: 'up' },
        plomo: { price: 23.45, change: '-0.5%', trend: 'down' },
        estano: { price: 312.60, change: '+1.8%', trend: 'up' }
      };
    }
  }
};

export default metalPricesService;
